<template>
    <div
      v-resize="onResize"
      id="container"
      ref="container"
      class="viewer"
      @mousedown="onPointerStart"
      @mousemove="onPointerMove"
      @mouseup="onPointerUp"
      @wheel="onWheel"
      @touchstart="onPointerStart"
      @touchend="onPointerUp"
      @touchmove="onPointerMove"
      @mouseleave="onPointerLeave"
    >
      <transition name="fade">
      <div class="swipe-image" v-if="!touchStarted && imageLoaded">
        <img height="50%" src="/hint_swipe.png"/>
      </div>
      </transition>
      <div class="download-image" v-if="imageLoaded" @click="downloadBlob">
        <v-icon dark x-large>mdi-download</v-icon>
      </div>
    </div>
</template>

<script>
import * as THREE from 'three';

export default {
  name: 'DeviceImage360',
  data() {
    return {
      objectURL: null,
      camera: null,
      scene: null,
      renderer: null,
      texture: null,
      mesh: null,
      geometry: null,
      isUserInteracting: false,
      onMouseDownMouseX: 0,
      onMouseDownMouseY: 0,
      lon: 0,
      lat: 0,
      phi: 0,
      onMouseDownLon: 0,
      onMouseDownLat: 0,
      theta: 0,
      controls: null,
      touchStarted: false,
      imageLoaded: false,
      initialized: false,
      downloadName: null,
    };
  },
  props: {
    image: {
      type: Object,
      required: true,
    },
  },
  watch: {
    async image(newval/* , oldval */) {
      await this.myFetch(newval.url);
    },
  },
  computed: {
    altText() {
      return `Thumbnail ${this.image.created}`;
    },
  },
  beforeDestroy() {
    if (this.material) this.material.dispose();
    if (this.geometry) this.geometry.dispose();
    if (this.texture) this.texture.dispose();
    if (this.scene) this.scene.dispose();
  },
  async mounted() {
    if (this.image.url !== '/noimage.png') {
      await this.myFetch(this.image.url);
    }
  },
  methods: {
    async myFetch(url) {
      const resp = await this.$authorizedFetch(url);
      const blob = await resp.blob();
      this.downloadName = url.split('/').pop();
      this.blob = blob;
      // this.downloadBlob(blob);
      const reader = new FileReader();
      reader.onloadend = () => {
        this.objectURL = reader.result;
        this.imageLoaded = true;
        if (!this.initialized) this.initThree();
        else this.updateTexture();
      };
      reader.readAsDataURL(blob); // converts the blob to base64 and calls onload
    },
    updateTexture() {
      // Free up texture memory
      // https://threejs.org/docs/#manual/en/introduction/How-to-dispose-of-objects
      if (this.texture) this.texture.dispose();

      // Load new texture
      new THREE.TextureLoader().load(this.objectURL, (texture) => {
        this.texture = texture;
        this.texture.minFilter = THREE.LinearFilter;
        this.texture.needsUpdate = true;
        this.material.map = this.texture;
      });
    },
    initThree() {
      const { container } = this.$refs;

      // Create camera
      this.camera = new THREE.PerspectiveCamera(
        75, container.clientWidth / container.clientHeight, 1, 1100,
      );
      this.camera.target = new THREE.Vector3(0, 0, 0);

      // Create scene
      this.scene = new THREE.Scene();

      // Create geometry
      this.geometry = new THREE.SphereBufferGeometry(500, 60, 40);
      // invert the geometry on the x-axis so that all of the faces point inward
      this.geometry.scale(-1, 1, 1);

      this.texture = new THREE.TextureLoader().load(this.objectURL);
      this.texture.minFilter = THREE.LinearFilter;

      // Create mesh material
      this.material = new THREE.MeshBasicMaterial({ map: this.texture });

      // Create mesh
      this.mesh = new THREE.Mesh(this.geometry, this.material);

      // add mesh to scene
      this.scene.add(this.mesh);

      // Create WebGL renderer
      this.renderer = new THREE.WebGLRenderer();
      this.renderer.setPixelRatio(window.devicePixelRatio);
      this.renderer.setSize(container.clientWidth, container.clientHeight);

      // Add canvas to DOM
      this.$refs.container.appendChild(this.renderer.domElement);
      this.update();
      this.initialized = true;
    },

    onResize() {
      const { container } = this.$refs;
      if (container && this.camera && this.renderer) {
        this.camera.aspect = container.clientWidth / container.clientHeight;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(container.clientWidth, container.clientHeight);
      }
    },

    onPointerStart(event) {
      this.touchStarted = true;
      this.isUserInteracting = true;
      const clientX = event.clientX || event.touches[0].clientX;
      const clientY = event.clientY || event.touches[0].clientY;
      this.onMouseDownMouseX = clientX;
      this.onMouseDownMouseY = clientY;
      this.onMouseDownLon = this.lon;
      this.onMouseDownLat = this.lat;
      event.preventDefault();
    },

    onPointerMove(event) {
      if (this.isUserInteracting === true) {
        const clientX = event.clientX || event.touches[0].clientX;
        const clientY = event.clientY || event.touches[0].clientY;
        this.lon = (this.onMouseDownMouseX - clientX) * 0.1 + this.onMouseDownLon;
        this.lat = (clientY - this.onMouseDownMouseY) * 0.1 + this.onMouseDownLat;
        event.preventDefault();
      }
    },

    onPointerUp() {
      this.isUserInteracting = false;
    },

    onWheel(event) {
      const fov = this.camera.fov + event.deltaY * 0.05;
      this.camera.fov = THREE.MathUtils.clamp(fov, 10, 105);
      this.camera.updateProjectionMatrix();
      event.preventDefault();
    },
    onPointerLeave() {
      this.isUserInteracting = false;
    },

    animate() {
      this.update();
    },

    update() {
      requestAnimationFrame(this.update);

      this.lat = Math.max(-85, Math.min(85, this.lat));
      this.phi = THREE.MathUtils.degToRad(90 - this.lat);
      this.theta = THREE.MathUtils.degToRad(this.lon);
      this.camera.target.x = 500 * Math.sin(this.phi) * Math.cos(this.theta);
      this.camera.target.y = 500 * Math.cos(this.phi);
      this.camera.target.z = 500 * Math.sin(this.phi) * Math.sin(this.theta);
      this.camera.lookAt(this.camera.target);

      // distortion
      // camera.position.copy( camera.target ).negate();

      this.renderer.render(this.scene, this.camera);
    },
    downloadBlob() {
      // Create an object URL for the blob object
      const url = URL.createObjectURL(this.blob);
      // Create a new anchor element
      const a = document.createElement('a');
      // Set the href and download attributes for the anchor element
      // You can optionally set other attributes like `title`, etc
      // Especially, if the anchor element will be attached to the DOM
      a.href = url;
      a.download = this.downloadName || 'download';
      // Click handler that releases the object URL after the element has been clicked
      // This is required for one-off downloads of the blob content
      const clickHandler = () => {
        setTimeout(() => {
          URL.revokeObjectURL(url);
          a.removeEventListener('click', clickHandler);
        }, 150);
      };
      // Add the click event listener on the anchor element
      // Comment out this line if you don't want a one-off download of the blob content
      a.addEventListener('click', clickHandler, false);
      // Programmatically trigger a click on the anchor element
      // Useful if you want the download to happen automatically
      // Without attaching the anchor element to the DOM
      // Comment out this line if you don't want an automatic download of the blob content
      a.click();
      // Return the anchor element
      // Useful if you want a reference to the element
      // in order to attach it to the DOM or use it in some other way
      return a;
    },
  },
};
</script>
<style scoped>

.viewer {
  width: 100%;
  height: 60vh;
  overflow: hidden;
  position: relative;
}

.swipe-image {
  width: inherit;
  height: inherit;
  position: absolute;
  top: 0;
  left: 0;
  background:rgba(0,0,0,0);
}

.download-image {
  width: 48px;
  height: 48px;
  position: absolute;
  right: 20px;
  bottom: 10px;
  background:rgba(0,0,0,0);
  cursor: pointer;
}

.swipe-image img {
  display: block;
  margin: auto;
  position: relative;
  top: 50%;
  transform: translateY(-50%);
}

.fade-enter-active, .fade-leave-active {
  transition: opacity .3s;
}
.fade-enter, .fade-leave-to {
  opacity: 0;
}
</style>
